import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ProgressPath from '../ui/ProgressPath';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { Flower2Icon, LeafIcon, CheckIcon } from 'lucide-react';
const InvestmentAssessment: React.FC = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [selectedOption, setSelectedOption] = useState<string>(assessmentData.investmentLevel || '');
  const investmentOptions = [{
    id: 'free-tier',
    title: 'Sacred Seed',
    description: 'Access to basic garden features and community support',
    price: 'Free',
    features: ['Daily reflection prompts', 'Basic garden progress tracking', 'Community forum access']
  }, {
    id: 'growth-tier',
    title: 'Garden Cultivator',
    description: 'Enhanced features for deeper healing and growth',
    price: '$9.99/month',
    features: ['All Sacred Seed features', 'Unlimited guided meditations', 'Advanced garden restoration quests', 'Personal growth analytics']
  }, {
    id: 'premium-tier',
    title: 'Master Gardener',
    description: 'Full access to all healing tools and personalized guidance',
    price: '$19.99/month',
    features: ['All Garden Cultivator features', 'Weekly personalized healing plans', 'Priority access to new gardens', 'One-on-one guidance sessions', 'Advanced Human Design insights']
  }];
  const handleContinue = () => {
    updateAssessmentData({
      investmentLevel: selectedOption
    });
    setCurrentStep(currentStep + 1);
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-656">
      <AnimatedBackground type="cosmic" data-id="element-657" />
      <ParticleEffect count={50} data-id="element-658" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-659">
        <LeafIcon size={80} className="text-green-200" data-id="element-660" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-661">
        <LeafIcon size={100} className="text-green-200" data-id="element-662" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-663">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-664" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-665">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-2" data-id="element-666">
          <span className="text-pink-300" data-id="element-667">Seven</span>After
        </h1>
        <ProgressPath totalSteps={7} currentStep={4} data-id="element-668" />
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-669">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-670"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-671"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-672">
            Your Garden Investment
          </h2>
          <p className="text-white text-center mb-6 relative z-10" data-id="element-673">
            Select the level of investment that feels aligned with your healing
            journey. This helps us understand your commitment level.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 relative z-10" data-id="element-674">
            {investmentOptions.map(option => <button key={option.id} onClick={() => setSelectedOption(option.id)} className={`
                  p-5 rounded-xl text-left transition-all border backdrop-blur-sm flex flex-col h-full
                  ${selectedOption === option.id ? 'bg-gradient-to-br from-pink-500/30 to-purple-500/30 border-pink-300 border-opacity-70 shadow-lg' : 'bg-white bg-opacity-5 border-white border-opacity-20 hover:bg-opacity-10'}
                `} data-id="element-675">
                <div className="mb-4" data-id="element-676">
                  <h3 className="text-xl font-bold text-white" data-id="element-677">
                    {option.title}
                  </h3>
                  <p className="text-white text-opacity-80 text-sm mt-1" data-id="element-678">
                    {option.description}
                  </p>
                  <div className="mt-2 text-lg font-medium text-pink-300" data-id="element-679">
                    {option.price}
                  </div>
                </div>
                <div className="flex-grow" data-id="element-680">
                  <h4 className="text-sm font-medium text-white mb-2" data-id="element-681">
                    Includes:
                  </h4>
                  <ul className="space-y-1" data-id="element-682">
                    {option.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-683">
                        <CheckIcon size={16} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" data-id="element-684" />
                        <span className="text-white text-opacity-80 text-sm" data-id="element-685">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
                {selectedOption === option.id && <div className="absolute top-2 right-2 bg-pink-500 rounded-full p-1" data-id="element-686">
                    <CheckIcon size={16} className="text-white" data-id="element-687" />
                  </div>}
              </button>)}
          </div>
          <button onClick={handleContinue} disabled={!selectedOption} className={`
              w-full py-4 rounded-xl transition-all duration-300 text-white font-semibold relative z-10
              ${selectedOption ? 'mystical-button glow bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer' : 'bg-gray-500 bg-opacity-50 cursor-not-allowed'}
            `} data-id="element-688">
            Continue Your Sacred Journey
          </button>
          {!selectedOption && <p className="text-center text-white text-opacity-60 mt-3 text-sm relative z-10" data-id="element-689">
              Please select an investment level to continue
            </p>}
        </div>
      </div>
    </div>;
};
export default InvestmentAssessment;